import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { BarChart3, Users, Briefcase, TrendingUp, CheckCircle2, XCircle, Clock } from 'lucide-react';

interface CampaignStat {
  id: string;
  title: string;
  country: string;
  status: string;
  totalApplicants: number;
  shortlisted: number;
  selected: number;
  rejected: number;
}

export default function Reports() {
  const { activeUserId } = useAuth();
  const [stats, setStats] = useState<CampaignStat[]>([]);
  const [summary, setSummary] = useState({ totalCampaigns: 0, totalApplicants: 0, totalSelected: 0, conversionRate: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeUserId) fetchReports();
  }, [activeUserId]);

  const fetchReports = async () => {
    try {
      // Get all campaigns
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id, title, country, status')
        .eq('recruiter_id', activeUserId!)
        .order('created_at', { ascending: false });

      if (!campaigns || campaigns.length === 0) {
        setLoading(false);
        return;
      }

      const campaignIds = campaigns.map(c => c.id);

      // Get jobs for these campaigns
      const { data: jobs } = await supabase
        .from('jobs')
        .select('id, campaign_id')
        .in('campaign_id', campaignIds);

      const jobIds = (jobs || []).map(j => j.id);

      // Get all applications
      const { data: apps } = await supabase
        .from('applications')
        .select('id, job_id, stage')
        .in('job_id', jobIds);

      const allApps = apps || [];

      // Build per-campaign stats
      const campaignStats: CampaignStat[] = campaigns.map(camp => {
        const campJobIds = (jobs || []).filter(j => j.campaign_id === camp.id).map(j => j.id);
        const campApps = allApps.filter(a => campJobIds.includes(a.job_id));

        return {
          id: camp.id,
          title: camp.title,
          country: camp.country,
          status: camp.status,
          totalApplicants: campApps.length,
          shortlisted: campApps.filter(a => a.stage === 'shortlisted').length,
          selected: campApps.filter(a => a.stage === 'selected' || a.stage === 'confirmed').length,
          rejected: campApps.filter(a => a.stage === 'rejected').length,
        };
      });

      setStats(campaignStats);

      const totalApps = campaignStats.reduce((s, c) => s + c.totalApplicants, 0);
      const totalSelected = campaignStats.reduce((s, c) => s + c.selected, 0);

      setSummary({
        totalCampaigns: campaigns.length,
        totalApplicants: totalApps,
        totalSelected,
        conversionRate: totalApps > 0 ? Math.round((totalSelected / totalApps) * 100) : 0,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in">
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>Reports</h1>
        <p className="text-slate-500 mt-1 text-[15px]">Campaign performance and conversion analytics.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Campaigns', value: summary.totalCampaigns, icon: <Briefcase size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Applicants', value: summary.totalApplicants, icon: <Users size={20} />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Selected', value: summary.totalSelected, icon: <CheckCircle2 size={20} />, color: 'text-violet-600', bg: 'bg-violet-50' },
          { label: 'Conversion', value: `${summary.conversionRate}%`, icon: <TrendingUp size={20} />, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
            <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center ${s.color} mb-3`}>
              {s.icon}
            </div>
            <p className="text-2xl font-black text-slate-900 tracking-tight">{s.value}</p>
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Campaign breakdown table */}
      <div>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Campaign Breakdown</h2>
        {stats.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
            <BarChart3 size={40} className="mx-auto text-slate-200 mb-3" />
            <p className="text-slate-400 text-[14px]">No campaign data yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
            {/* Table header */}
            <div className="grid grid-cols-7 gap-2 px-5 py-3 bg-slate-50 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              <div className="col-span-2">Campaign</div>
              <div className="text-center">Status</div>
              <div className="text-center">Applicants</div>
              <div className="text-center">Shortlisted</div>
              <div className="text-center">Selected</div>
              <div className="text-center">Rejected</div>
            </div>

            {stats.map((camp, i) => {
              const conversion = camp.totalApplicants > 0 ? Math.round((camp.selected / camp.totalApplicants) * 100) : 0;
              return (
                <div key={camp.id} className={`grid grid-cols-7 gap-2 px-5 py-4 items-center ${i > 0 ? 'border-t border-slate-100' : ''} hover:bg-slate-50/50 transition-colors`}>
                  <div className="col-span-2 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-800 truncate">{camp.title}</p>
                    <p className="text-[11px] text-slate-400">{camp.country}</p>
                  </div>
                  <div className="text-center">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${camp.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                        camp.status === 'completed' ? 'bg-blue-50 text-blue-700' :
                          'bg-slate-100 text-slate-500'
                      }`}>
                      {camp.status}
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="text-[14px] font-bold text-slate-800">{camp.totalApplicants}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-[14px] font-bold text-amber-600">{camp.shortlisted}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-[14px] font-bold text-emerald-600">{camp.selected}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-[14px] font-bold text-red-500">{camp.rejected}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
